<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <!-- Header -->
    <div class="flex flex-col gap-1 mb-2 p-4 border rounded-2xl bg-white">
      <div class="flex items-center justify-between gap-2">
        <span class="font-medium text-lg">{{ pack.templateName || 'כרטיסייה' }}</span>
        <span
          class="text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
          :class="statusChip.classes"
        >{{ statusChip.label }}</span>
      </div>
      <span v-if="customerName" class="text-sm text-gray-600">{{ customerName }}</span>

      <div class="flex items-center gap-1.5 mt-2">
        <div class="flex gap-1">
          <span
            v-for="i in pack.totalUses"
            :key="i"
            class="w-2 h-2 rounded-full"
            :class="i <= usedCount ? 'bg-primary' : 'bg-gray-200'"
          />
        </div>
        <span class="text-xs text-gray-500 mr-auto">
          {{ usedCount }}/{{ pack.totalUses }} מומשו
        </span>
      </div>

      <div v-if="pack.expiresAt" class="text-xs text-gray-500 mt-1">
        בתוקף עד {{ formattedExpiry }}
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-col gap-2">
      <button
        v-if="canRedeem"
        v-wave
        class="w-full py-3 px-4 rounded-xl bg-primary text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        :disabled="redeeming"
        @click="confirmRedeem"
      >
        <i class="pi pi-check-circle" />
        <span>מימוש שירות אחד</span>
      </button>

      <button
        v-if="canSendPayment"
        v-wave
        class="w-full py-3 px-4 rounded-xl border border-primary text-primary font-medium flex items-center justify-center gap-2"
        @click="openSendPayment"
      >
        <i class="pi pi-credit-card" />
        <span>שליחת בקשת תשלום</span>
      </button>

      <button
        v-if="canCancel"
        v-wave
        class="w-full py-3 px-4 rounded-xl border border-red-200 text-red-600 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        :disabled="cancelling"
        @click="confirmCancel"
      >
        <i class="pi pi-times" />
        <span>ביטול הכרטיסייה</span>
      </button>

      <div
        v-if="!canRedeem && !canSendPayment && !canCancel"
        class="text-sm text-gray-500 text-center p-4"
      >
        אין פעולות זמינות לכרטיסייה זו
      </div>
    </div>

    <Button
      class="mt-auto"
      severity="secondary"
      text
      label="סגירה"
      @click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import { BundlePackStatus, ICustomerBundlePackWithCustomer } from '@torim-app/types';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { useDrawer } from '@torim-app/shared-components';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import { DateTime } from 'luxon';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';

const props = defineProps({
  pack: {
    type: Object as PropType<ICustomerBundlePackWithCustomer>,
    required: true
  }
});

const emit = defineEmits(['close', 'changed']);
const { openDrawer, closeDrawer } = useDrawer();
const toast = useToast();
const confirm = useConfirm();

const redeeming = ref(false);
const cancelling = ref(false);

const customerName = computed(() => props.pack.customerName);
const usedCount = computed(() => Math.max(0, (props.pack.totalUses ?? 0) - (props.pack.remainingUses ?? 0)));

const formattedExpiry = computed(() => {
  if (!props.pack.expiresAt) return '';
  return DateTime.fromISO(String(props.pack.expiresAt)).toFormat('dd.MM.yyyy');
});

const statusChip = computed(() => {
  switch (props.pack.status) {
    case BundlePackStatus.Active:
      return { label: 'פעיל', classes: 'bg-green-100 text-green-700' };
    case BundlePackStatus.Exhausted:
      return { label: 'נוצל', classes: 'bg-gray-100 text-gray-600' };
    case BundlePackStatus.Expired:
      return { label: 'פג תוקף', classes: 'bg-red-100 text-red-700' };
    case BundlePackStatus.PendingPayment:
      return { label: 'ממתין לתשלום', classes: 'bg-amber-100 text-amber-700' };
    case BundlePackStatus.Cancelled:
      return { label: 'בוטל', classes: 'bg-gray-100 text-gray-500' };
    default:
      return { label: '', classes: '' };
  }
});

const canRedeem = computed(() =>
  props.pack.status === BundlePackStatus.Active && (props.pack.remainingUses ?? 0) > 0
);

const canSendPayment = computed(() => props.pack.status === BundlePackStatus.PendingPayment);

// Cancel only when nothing has been redeemed yet (matches backend constraint).
const canCancel = computed(() =>
  props.pack.status !== BundlePackStatus.Cancelled &&
  props.pack.status !== BundlePackStatus.Exhausted &&
  props.pack.remainingUses === props.pack.totalUses
);

const confirmRedeem = () => {
  confirm.require({
    message: 'לממש שירות אחד מהכרטיסייה?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      redeeming.value = true;
      try {
        await BundlePacksApi.redeemBundlePack(props.pack._id!);
        toast.success('המימוש נרשם');
        emit('changed');
        emit('close');
      } catch (e) {
        toast.error('שגיאה במימוש');
      } finally {
        redeeming.value = false;
      }
    }
  });
};

const confirmCancel = () => {
  confirm.require({
    message: 'לבטל את הכרטיסייה?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      cancelling.value = true;
      try {
        await BundlePacksApi.cancelPack(props.pack._id!);
        toast.success('הכרטיסייה בוטלה');
        emit('changed');
        emit('close');
      } catch (e) {
        toast.error('שגיאה בביטול');
      } finally {
        cancelling.value = false;
      }
    }
  });
};

const openSendPayment = () => {
  openDrawer(
    PaymentOptions,
    {
      uuid: props.pack._id,
      payFor: 'bundle-pack',
      onClose: (shouldLoad: boolean) => {
        closeDrawer();
        if (shouldLoad) emit('changed');
        emit('close');
      }
    },
    { title: 'אפשרויות תשלום' }
  );
};
</script>
